import { useEffect, useRef, useState } from "react";
import { set, useForm } from "react-hook-form";
import { ProjectRegisterForm } from "../../../models/ProjectForm";
import projectFormService from "../../../services/forms/projectForm.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CustomInput from "../../../components/shared/Input/CustomInput";
import ProjectMember from "../../../components/form/ProjectMember";
import ProjectAdvisor from "../../../components/form/ProjectAdvisor";
import { Button } from "@material-tailwind/react";
import ModalForm from "../../../components/form/ModalForm";
import { SelectForm, SelectFormOptionProp } from "../../../components/shared/Input/SelectForm";
import { HttpStatusCode } from "../../../models/HttpStatusCodes";
import useGlobalState from "../../../context/GlobalState";
import { AlertTypes } from "../../../models/Enums";
import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { EducationProgram } from "../../../models/EducationPrograms";
import educationProgramsService from "../../../services/educationPrograms.service";
import StateHandler from "../../../components/StateHandler/StateHandler";
import periodsService from "../../../services/periods.service";
import './ProjectRegister.css';


const ProjectRegister = () => {
    const { setAlert, isLoading, setIsLoading } = useGlobalState();
        const { register, handleSubmit, unregister, formState: { errors }, setValue, getValues, control, reset } = useForm<ProjectRegisterForm>({ mode: 'onChange' });
    const [members, setMembers] = useState(0);
    const [secondAdvisor, setSecondAdvisor] = useState(false);
    const [eduPrograms, setEduPrograms] = useState<EducationProgram[]>([{} as EducationProgram]);
    const [eduProgramsOptions, setEduProgramsOptions] = useState<SelectFormOptionProp[]>([{ label: '[...]', value: 0 } as SelectFormOptionProp]);
    const [currentPeriod, setCurrentPeriod] = useState("[...]");
    const [isActive, setIsActive] = useState<boolean>(true);
    const [enabled, setEnabled] = useState(true);
    const [completed, setCompleted] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();
    const categories = [
        { label: 'Base Tecnológica', value: 'Base Tecnológica' },
        { label: 'Emprendimiento Social y Ambiental', value: 'Emprendimiento Social y Ambiental' },
        { label: 'Emprendimiento Tradicional', value: 'Emprendimiento Tradicional' }
    ];

    const validateProjectName = async (name: string): Promise<boolean|string> => {
        setEnabled(false);
        return new Promise(resolve => {
            clearTimeout(debounceRef.current); // Limpia el timeout anterior para evitar múltiples llamadas
            // Establece un nuevo timeout para realizar la verificación después de 500ms
            debounceRef.current = setTimeout(async () => {
                const exists = await projectFormService.verifyProjectName(name);
                resolve(exists ? 'El nombre del proyecto ya existe' : true); //Si el proyecto existe, retorna un mensaje de error, si no, retorna true
                setEnabled(true);
            }, 500)
        });
    }

    const handleFormSubmit = async () => {
        setEnabled(false);
        setIsLoading(true);
        let formData = new FormData();
        let data = getValues();

        formData.append('projectSubject', data.projectSubject);
        formData.append('projectCategory', data.projectCategory);
        formData.append('projectName', data.projectName);
        
        if (data.projectFile) {
            formData.append('projectFile', data.projectFile[0]);
        } else {
            setAlert('Debes agregar tu archivo de proyecto', AlertTypes.WARNING);
            setEnabled(true);
            setIsLoading(false);
            return;
        }

        data.projectMembers.forEach((member, index) => {
            formData.append(`projectMembers[${index}][name]`, member.name);
            formData.append(`projectMembers[${index}][email]`, member.email);
            formData.append(`projectMembers[${index}][gender]`, member.gender);
            formData.append(`projectMembers[${index}][eduProgram]`, member.eduProgram.toString());
        });

        data.projectAdvisors.forEach((advisor, index) => {
            formData.append(`projectAdvisors[${index}][name]`, advisor.name);
            formData.append(`projectAdvisors[${index}][email]`, advisor.email);
        }
        );

        let resp = await projectFormService.createProject(formData);
        if (resp.status === HttpStatusCode.Ok) {
            setAlert('Proyecto creado con exito', AlertTypes.SUCCESS);
            setCompleted(true);
            reset();
        }
        setIsLoading(false);
        setEnabled(true);
    }

    const deleteMember = (index: number) => {
        unregister(`projectMembers.${index}`);
        setMembers(members - 1);
    }

    const getEducationPrograms = async () => {
        let resp = await educationProgramsService.getEducationPrograms();
        if (resp) {
            setEduPrograms(resp);
        }
    }

    const getCurrentPeriod = async () => {
        let res = await periodsService.getCurrentPeriod();
        if (res) {
            setCurrentPeriod(res.id);
            setIsActive(res.isActive);
        }
    };

    const formatEducationProgramsOptions = () => {
        let options = eduPrograms.map((program) => {
            return { label: program.name, value: program.id, disabled: !program.isActive } as SelectFormOptionProp;
        });
        setEduProgramsOptions(options);
    }

    const unSetSecondAdvisor = () => {
        unregister("projectAdvisors.1");
        setSecondAdvisor(false);
    }

    useEffect(() => {
        getEducationPrograms();
        getCurrentPeriod();
    }, []);

    useEffect(() => {
        formatEducationProgramsOptions();
    }, [eduPrograms]);

    if (!isActive) {
        return <StateHandler title={"La Expo Emprendedores " + currentPeriod + " finalizó su registro."} subTitle="Sigue al pendiente de fututos eventos siguiendo la pagina de FCITEC." showFcitecLink />;
    }

    if (completed) {
        return <StateHandler title="Gracias por completar el formulario!" subTitle="Los integrantes recibiran un correo electrónico confirmando la informacion." showFcitecLink />;
    }

    return (
        <div className="ProjectRegister">
            <div className="register-header">
                <div>
                    <img src="/emprendedores/UABClogos/FCITEC.png" alt="logo" />
                    <h1>Registro de Proyecto</h1>
                </div>
            </div>
            <form className="form" encType="multipart/form-data">
                <div className="form-message">
                    <h2>¡Bienvenido a Expo Emprendedores {currentPeriod}!</h2>
                    <div className="line-bar"></div>
                    <p>Por favor, completa el siguiente formulario de registro con atención y precisión.</p>
                    <p>Asegúrate de ingresar tu nombre y apellido exactamente como aparecen en tus documentos oficiales, así como una dirección de correo electrónico válida y activa.</p>
                    <p>Además, si estás representando a un proyecto o empresa, asegúrate de proporcionar el nombre correspondiente de manera precisa. Esto garantizará que la información en tus constancias sea correcta y esté alineada con tus datos personales.</p>
                    <p> Estamos ansiosos por conocerte y brindarte todo el apoyo que necesitas para hacer de tu participación en Expo Emprendedores {currentPeriod} una experiencia exitosa.</p>
                </div>

                <div className="ProjectInfoContainer form-data">
                    <div className="ContainerHeader">
                        <h1>Información del Proyecto</h1>
                    </div>
                    <SelectForm label="null" control={control} options={eduProgramsOptions} field="null" visible={false} />
                    <div className="ProjectInfo">
                        <CustomInput capitalize="uppercase" errors={errors} label="Nombre del Proyecto" field="projectName" register={register} rules={{ required: true, maxLength: 150, validate: validateProjectName }}/>
                        <CustomInput capitalize="uppercase" errors={errors} label="Asignatura del Proyecto" field="projectSubject" register={register} rules={{ required: true, maxLength: 150 }} />
                        <SelectForm errors={errors} label="Categoria del Proyecto" control={control} options={categories} field="projectCategory" rules={{ required: true }} />
                        <ModalForm errors={errors} field="projectFile" register={register} setValue={setValue} rules={{ required: true }} />
                    </div>
                </div>
                <div className="ProjectMembersContainer form-data">
                    <div className="ContainerHeader">
                        <h1>Información de los Integrantes</h1>
                    </div>
                    <div className="ProjectMembers">
                        <ProjectMember eduPrograms={eduProgramsOptions} register={register} index={0} visible={members >= 0} errors={errors} control={control} />
                        <ProjectMember eduPrograms={eduProgramsOptions} register={register} index={1} visible={members >= 1} errors={errors} control={control} />
                        <ProjectMember eduPrograms={eduProgramsOptions} register={register} index={2} visible={members >= 2} errors={errors} control={control} />
                        <ProjectMember eduPrograms={eduProgramsOptions} register={register} index={3} visible={members >= 3} errors={errors} control={control} />
                        <ProjectMember eduPrograms={eduProgramsOptions} register={register} index={4} visible={members >= 4} errors={errors} control={control} />
                    </div>
                </div>
                <div className="btnContainer">
                    <Button className="btn-add-border" variant="outlined" placeholder="" onClick={() => deleteMember(members)} hidden={members === 0}><FontAwesomeIcon icon={faMinus} /> Quitar Integrante</Button>
                    <Button className="btn-add-border" hidden={members >= 4} variant="outlined" placeholder="" onClick={() => { setMembers(members + 1) }}>Agregar Integrante <FontAwesomeIcon icon={faPlus} /></Button>
                </div>

                <div className="ProjectAdvisorsContainer form-data">
                    <div className="ContainerHeader">
                        <h1>Información de los Asesores</h1>
                    </div>
                    <div className="ProjectAdvisors">
                        <ProjectAdvisor register={register} index={0} visible={true} errors={errors} />
                        {
                            secondAdvisor ? (
                                <>
                                    <ProjectAdvisor register={register} index={1} visible={secondAdvisor} errors={errors} />
                                    <Button className="btn-add-border" variant="outlined" placeholder="" onClick={() => { unSetSecondAdvisor() }}>Quitar segundo Asesor <FontAwesomeIcon icon={faMinus} /></Button>
                                </>
                            ) : (
                                <>
                                    <Button className="btn-add-border"  variant="outlined" placeholder="" onClick={() => { setSecondAdvisor(true) }}>Agregar Otro Asesor <FontAwesomeIcon icon={faPlus} /></Button>
                                </>
                            )
                        }
                    </div>
                </div>

                <Button loading={isLoading} className="btn-send" disabled={!enabled} placeholder="" variant="gradient" onClick={handleSubmit(() => handleFormSubmit())} onMouseDown={(e) => e.preventDefault()}>Registrar</Button>
            </form>
        </div>
    );
}

export default ProjectRegister;